// frontend/src/api/notifications.ts
import client from './client';

/**
 * Notification event from backend
 */
export interface NotificationEvent {
  id: string;
  user_id: string;
  event_type: string;
  title: string;
  body: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  is_read: boolean;
  alert_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/**
 * User notification delivery preferences
 */
export interface NotificationPreferences {
  user_id: string;
  channel_email_enabled: boolean;
  channel_webhook_enabled: boolean;
  channel_in_app_enabled: boolean;
  email_address?: string;
  webhook_url?: string;
  webhook_format?: 'json' | 'form-encoded';
  max_emails_per_day: number;
  quiet_hours_start?: string; // HH:MM
  quiet_hours_end?: string; // HH:MM
  created_at?: string;
  updated_at?: string;
}

/**
 * Delivery statistics
 */
export interface DeliveryStatistics {
  emails_sent_today: number;
  emails_sent_week: number;
  delivery_success_rate: number;
  last_delivery_at?: string;
}

/**
 * Test delivery result
 */
export interface TestDeliveryResult {
  channel: string;
  success: boolean;
  error?: string;
}

// ============ API Functions ============

/**
 * GET /notifications/:userId
 * Get user's notification events with pagination and filtering
 */
export async function getNotifications(
  userId: string,
  filters?: {
    limit?: number;
    offset?: number;
    is_read?: boolean;
    event_type?: string;
  }
): Promise<{
  events: NotificationEvent[];
  pagination: {
    offset: number;
    limit: number;
    total: number;
  };
}> {
  const params: Record<string, any> = {};
  if (filters?.limit) params.limit = filters.limit;
  if (filters?.offset !== undefined) params.offset = filters.offset;
  if (filters?.is_read !== undefined) params.is_read = filters.is_read.toString();
  if (filters?.event_type) params.event_type = filters.event_type;

  return client.get(`/notifications/${userId}`, { params });
}

/**
 * POST /notifications/events/:userId
 * Create a notification event (system use)
 */
export async function createNotification(
  userId: string,
  notification: {
    event_type: string;
    title: string;
    body: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    delivery_channels?: string[];
    alert_id?: string;
    metadata?: Record<string, any>;
  }
): Promise<{
  event: NotificationEvent;
  deliveryCount: number;
  message: string;
}> {
  return client.post(`/notifications/events/${userId}`, notification);
}

/**
 * PUT /notifications/:userId/:notificationId/read
 * Mark notification as read
 */
export async function markAsRead(userId: string, notificationId: string): Promise<{ message: string }> {
  return client.put(`/notifications/${userId}/${notificationId}/read`);
}

/**
 * DELETE /notifications/:userId/:notificationId
 * Delete/archive a notification
 */
export async function deleteNotification(userId: string, notificationId: string): Promise<{ message: string }> {
  return client.delete(`/notifications/${userId}/${notificationId}`);
}

/**
 * GET /notifications/:userId/preferences
 * Get user notification delivery preferences
 */
export async function getPreferences(userId: string): Promise<NotificationPreferences> {
  return client.get(`/notifications/${userId}/preferences`);
}

/**
 * PATCH /notifications/:userId/preferences
 * Update user notification delivery preferences
 */
export async function updatePreferences(
  userId: string,
  preferences: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  return client.patch(`/notifications/${userId}/preferences`, preferences);
}

/**
 * POST /notifications/:userId/test-delivery
 * Test notification delivery for specified channels
 */
export async function testDelivery(
  userId: string,
  channels: string[]
): Promise<TestDeliveryResult[]> {
  return client.post(`/notifications/${userId}/test-delivery`, { channels });
}

/**
 * GET /notifications/:userId/stats
 * Get delivery statistics
 */
export async function getDeliveryStats(userId: string): Promise<DeliveryStatistics> {
  return client.get(`/notifications/${userId}/stats`);
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(userId: string): Promise<{ message: string }> {
  return client.post(`/notifications/${userId}/mark-all-read`);
}

export interface NotificationPreferenceMatrix {
  priceAlert: { inApp: boolean; push: boolean; email: boolean };
  defiHealth: { inApp: boolean; push: boolean; email: boolean };
  portfolioDrift: { inApp: boolean; push: boolean; email: boolean };
}

export type PushReadiness = 'unsupported' | 'blocked' | 'available' | 'subscribed';
export type EmailReadiness = 'verified' | 'unverified' | 'missing';
export type DeliveryReadiness = 'ready' | 'email-unverified' | 'email-missing' | 'environment-blocked';

export interface NotificationChannelStatus {
  push: 'enabled' | 'blocked' | 'unsubscribed';
  email: 'verified' | 'pending';
  pushReadiness: PushReadiness;
  emailReadiness: EmailReadiness;
  pushSubscriptionCount: number;
  emailAddressConfigured: boolean;
}

export interface NotificationPreferencesResponseV2 {
  preferences: NotificationPreferenceMatrix;
  channelStatus: NotificationChannelStatus;
  defaultsApplied: boolean;
  preferenceSource: 'stored' | 'defaulted';
  updatedAt?: string;
}

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent?: string;
  /** 'ios' | 'android' for native Capacitor apps; 'web' for browser VAPID */
  platform?: 'ios' | 'android' | 'web';
  /** FCM/APNs device token (native only; same value as endpoint for native) */
  nativeToken?: string;
}

function getPushPermissionHeaders(
  pushPermission?: 'unsupported' | 'blocked' | 'default' | 'granted'
) {
  return pushPermission ? { 'x-push-permission': pushPermission } : undefined;
}

export async function getNotificationPreferencesV2(
  pushPermission?: 'unsupported' | 'blocked' | 'default' | 'granted'
): Promise<NotificationPreferencesResponseV2> {
  const res = await client.get<NotificationPreferencesResponseV2>('/notifications/preferences', {
    headers: getPushPermissionHeaders(pushPermission),
  });
  return res.data;
}

export async function updateNotificationPreferencesV2(
  preferences: Partial<NotificationPreferenceMatrix>,
  pushPermission?: 'unsupported' | 'blocked' | 'default' | 'granted'
): Promise<{
  success: boolean;
  preferences: NotificationPreferenceMatrix;
  updatedAt: string;
  defaultsApplied: boolean;
  channelStatus: NotificationChannelStatus;
  saveResult: {
    scope: 'matrix';
    status: 'saved';
    changedKeys: string[];
  };
}> {
  const res = await client.patch<{
    success: boolean;
    preferences: NotificationPreferenceMatrix;
    updatedAt: string;
    defaultsApplied: boolean;
    channelStatus: NotificationChannelStatus;
    saveResult: {
      scope: 'matrix';
      status: 'saved';
      changedKeys: string[];
    };
  }>(
    '/notifications/preferences',
    { preferences },
    { headers: getPushPermissionHeaders(pushPermission) }
  );
  return res.data;
}

export async function subscribeToPushNotifications(
  payload: PushSubscriptionPayload
): Promise<{
  success: boolean;
  subscription: { id: string; endpoint: string; isActive: boolean; createdAt: string };
}> {
  return client.post('/notifications/subscribe', payload);
}

// ─── Weekly Alert Digest Opt-In (Chunk 81) ────────────────────────────────────

export async function getWeeklyDigestPreference(): Promise<{
  weeklyAlertDigestEnabled: boolean;
  defaultsApplied: boolean;
  preferenceSource: 'stored' | 'defaulted';
  deliveryReadiness: DeliveryReadiness;
  updatedAt?: string;
}> {
  const res = await client.get<{
    weeklyAlertDigestEnabled: boolean;
    defaultsApplied: boolean;
    preferenceSource: 'stored' | 'defaulted';
    deliveryReadiness: DeliveryReadiness;
    updatedAt?: string;
  }>('/notifications/preferences/digest');
  return res.data;
}

export async function updateWeeklyDigestPreference(
  enabled: boolean
): Promise<{
  success: boolean;
  weeklyAlertDigestEnabled: boolean;
  updatedAt?: string;
  deliveryReadiness: DeliveryReadiness;
  saveResult: {
    scope: 'digest';
    status: 'saved';
  };
}> {
  const res = await client.patch<{
    success: boolean;
    weeklyAlertDigestEnabled: boolean;
    updatedAt?: string;
    deliveryReadiness: DeliveryReadiness;
    saveResult: {
      scope: 'digest';
      status: 'saved';
    };
  }>(
    '/notifications/preferences/digest',
    { weeklyAlertDigestEnabled: enabled }
  );
  return res.data;
}

// ─── Auth-service Web Push Subscription (Chunk 201) ──────────────────────────

export async function registerPushSubscriptionWithAuth(
  userId: string,
  payload: PushSubscriptionPayload
): Promise<{
  success: boolean;
  subscription: { id: string; endpoint: string; isActive: boolean; createdAt: string };
}> {
  const res = await client.post<{
    success: boolean;
    subscription: { id: string; endpoint: string; isActive: boolean; createdAt: string };
  }>(`/auth/users/${userId}/push-subscription`, payload);
  return res.data;
}

export async function unregisterPushSubscriptionWithAuth(
  userId: string,
  endpoint: string
): Promise<{ success: boolean; id: string }> {
  const res = await client.delete<{ success: boolean; id: string }>(
    `/auth/users/${userId}/push-subscription`,
    { data: { endpoint } }
  );
  return res.data;
}
